import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { TrendingUp, TrendingDown, Award, AlertTriangle, Banknote, Fuel, Receipt, Calendar, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const txt = (lang, fr, ru, md, uk) => ({ fr, ru, md, uk }[lang] || fr);

export default function SalaryCard({ compact = false, userId = null, userName = null, month = null, year = null }) {
  const { user, isPreviewMode } = useAuth();
  const { language } = useLanguage();
  const [salary, setSalary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSalary = async () => {
      try {
        let res;
        // Mois demande par la page (ex. selecteur de la page Equipe), sinon mois en cours
        const params = {};
        if (month) params.month = month;
        if (year) params.year = year;
        if (userId) {
          // Manager viewing a specific employee's salary
          res = await axios.get(`${API}/salary/user/${userId}`, { params });
        } else {
          // Employee viewing their own salary
          res = await axios.get(`${API}/salary/my-monthly`, { params });
        }
        setSalary(res.data);
      } catch (e) {
        console.error('Salary fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    setLoading(true);
    fetchSalary();
  }, [userId, month, year]);

  if (loading) {
    return (
      <div className="card-base p-4 animate-pulse">
        <div className="h-16 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  if (!salary || !salary.daily_rate) return null;

  const { daily_rate, days_worked, gross_salary, total_bonus, total_fuel, total_expenses, total_penalties, total_advances, net_salary, penalties, bonus_details, advances } = salary;
  const displayName = userName || salary.user_name || '';

  const months = {
    fr: ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'],
    ru: ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'],
    md: ['Ianuarie','Februarie','Martie','Aprilie','Mai','Iunie','Iulie','August','Septembrie','Octombrie','Noiembrie','Decembrie'],
    uk: ['Січень','Лютий','Березень','Квітень','Травень','Червень','Липень','Серпень','Вересень','Жовтень','Листопад','Грудень']
  };
  const monthName = (months[language] || months.fr)[salary.month - 1];

  if (compact) {
    return (
      <div data-testid={`salary-card-compact${userId ? `-${userId}` : ''}`} className="card-base p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Banknote className="w-5 h-5 text-white" />
            </div>
            <div>
              {displayName && (
                <p className="text-[10px] text-emerald-500 font-medium truncate max-w-[120px]">{displayName}</p>
              )}
              <p className="text-xs text-emerald-600 font-medium uppercase tracking-wide">
                {txt(language, 'Salaire', 'Зарплата', 'Salariu', 'Зарплата')} {monthName}
              </p>
              <p className="text-xl font-bold text-emerald-800">{net_salary.toFixed(0)}€</p>
            </div>
          </div>
          <div className="text-right text-xs text-emerald-600 space-y-0.5">
            <p>{days_worked}j × {daily_rate}€</p>
            {total_bonus > 0 && <p className="text-green-600 font-semibold">+{total_bonus}€ {txt(language, 'prime', 'премия', 'primă', 'премія')}</p>}
            {total_penalties > 0 && <p className="text-red-600 font-semibold">-{total_penalties}€ {txt(language, 'pénalité', 'штраф', 'penalitate', 'штраф')}</p>}
            {(total_advances || 0) > 0 && <p className="text-amber-600 font-semibold">-{total_advances}€ {txt(language, 'acompte', 'аванс', 'avans', 'аванс')}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`salary-card${userId ? `-${userId}` : ''}`} className="card-base overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-5">
        <div className="flex items-center justify-between">
          <div>
            {displayName && (
              <p className="text-slate-400 text-xs flex items-center gap-1 mb-1">
                <User className="w-3 h-3" /> {displayName}
              </p>
            )}
            <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold mb-1">
              {userId 
                ? txt(language, 'Salaire', 'Зарплата', 'Salariu', 'Зарплата')
                : txt(language, 'Mon salaire', 'Моя зарплата', 'Salariul meu', 'Моя зарплата')
              } — {monthName}
            </p>
            <p className="text-3xl font-bold text-white tracking-tight">{net_salary.toFixed(2)}€</p>
          </div>
          <div className="w-14 h-14 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
            {net_salary >= gross_salary ? (
              <TrendingUp className="w-7 h-7 text-emerald-400" />
            ) : (
              <TrendingDown className="w-7 h-7 text-amber-400" />
            )}
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="p-5 space-y-3">
        {/* Gross */}
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>{txt(language, 'Salaire brut', 'Оклад', 'Salariu brut', 'Оклад')}</span>
          </div>
          <span className="text-sm font-semibold text-gray-800">
            {gross_salary.toFixed(2)}€
            <span className="text-xs text-gray-400 ml-1">({days_worked}j × {daily_rate}€)</span>
          </span>
        </div>

        {/* Bonus - highlighted! */}
        {total_bonus > 0 && (
          <div className="flex items-center justify-between py-2 px-3 bg-green-50 rounded-xl border border-green-200">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <Award className="w-4 h-4" />
              <span className="font-semibold">{txt(language, 'Primes', 'Премии', 'Prime', 'Премії')}</span>
            </div>
            <span className="text-sm font-bold text-green-700">+{total_bonus.toFixed(2)}€</span>
          </div>
        )}
        {bonus_details && bonus_details.length > 0 && (
          <div className="pl-6 space-y-1">
            {bonus_details.map((b, i) => (
              <p key={i} className="text-xs text-green-600 flex items-center gap-1">
                <Award className="w-3 h-3" />
                {new Date(b.date + 'T12:00:00').toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'ru' ? 'ru-RU' : language === 'uk' ? 'uk-UA' : 'ro-MD', { day: 'numeric', month: 'short' })}
                : +{b.amount}€{b.reason ? ` — ${b.reason}` : ''}
              </p>
            ))}
          </div>
        )}

        {/* Penalties - highlighted red */}
        {total_penalties > 0 && (
          <>
            <div className="flex items-center justify-between py-2 px-3 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-center gap-2 text-sm text-red-700">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-semibold">{txt(language, 'Pénalités', 'Штрафы', 'Penalități', 'Штрафи')}</span>
              </div>
              <span className="text-sm font-bold text-red-700">-{total_penalties.toFixed(2)}€</span>
            </div>
            <div className="pl-6 space-y-1">
              {penalties.map((p, i) => (
                <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {p.reason} : -{p.amount}€
                </p>
              ))}
            </div>
          </>
        )}

        {/* Advances (acomptes) */}
        {(total_advances || 0) > 0 && (
          <>
            <div className="flex items-center justify-between py-2 px-3 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-center gap-2 text-sm text-amber-700">
                <Banknote className="w-4 h-4" />
                <span className="font-semibold">{txt(language, 'Acomptes', 'Авансы', 'Avansuri', 'Аванси')}</span>
              </div>
              <span className="text-sm font-bold text-amber-700">-{total_advances.toFixed(2)}€</span>
            </div>
            {advances && advances.length > 0 && (
              <div className="pl-6 space-y-1">
                {advances.map((a, i) => (
                  <p key={i} className="text-xs text-amber-600 flex items-center gap-1">
                    <Banknote className="w-3 h-3" />
                    {new Date(a.date + 'T12:00:00').toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'ru' ? 'ru-RU' : language === 'uk' ? 'uk-UA' : 'ro-MD', { day: 'numeric', month: 'short' })}
                    : -{a.amount}€{a.note ? ` (${a.note})` : ''}
                  </p>
                ))}
              </div>
            )}
          </>
        )}

        {/* Fuel deduction */}
        {total_fuel > 0 && (
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Fuel className="w-4 h-4" />
              <span>{txt(language, 'Carburant', 'Топливо', 'Combustibil', 'Паливо')}</span>
            </div>
            <span className="text-sm font-semibold text-red-600">-{total_fuel.toFixed(2)}€</span>
          </div>
        )}

        {/* Expenses deduction */}
        {total_expenses > 0 && (
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Receipt className="w-4 h-4" />
              <span>{txt(language, 'Frais', 'Расходы', 'Cheltuieli', 'Витрати')}</span>
            </div>
            <span className="text-sm font-semibold text-red-600">-{total_expenses.toFixed(2)}€</span>
          </div>
        )}

        {/* Divider + NET */}
        <div className="border-t-2 border-dashed border-gray-200 pt-3">
          <div className="flex items-center justify-between">
            <span className="text-base font-bold text-gray-800">
              {txt(language, 'TOTAL NET', 'ИТОГО', 'TOTAL NET', 'РАЗОМ')}
            </span>
            <span className={`text-xl font-bold ${net_salary >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {net_salary.toFixed(2)}€
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
